import React, { useState } from "react";
import { TextField, Button, Checkbox, Grid, Card, Typography, Box, FormControl, IconButton, List, ListItem } from "@mui/material";
import { attachments, InspectionAttachments, InspectionColumn} from "../../types/assetTypes";
import { CustomMessage } from "../../const/Spinner";
import { enqueueSnackbar } from "notistack";
import { setLoading } from "../../store/CommonSlice";
import { saveInspectionData, updateInspectionData, uploadFiles } from "../../api/Services/assetServices";
import { useDispatch, useSelector } from "react-redux";
import { FILE_DOWNLOAD_PREVIEW } from "../../api/endpoint";
import ImagePreview from "../../Component/ImagePreview";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import { RootState } from "../../store/Reduxstore";

interface InspectionEditingFormProps {
  setEditOpen: (open: boolean) => void;
  inspectionTemplateData: {
    inspectionColumnId: number;
    inspectionColumnName: string;
    isChecked: boolean;
    commands?: string | null;
    attachments: attachments;
    inspectionAttachments: InspectionAttachments[];
  }[];
  selectedRowData:{
    inspectionReportingId: number;
    assetName: string;
    service: string;
    serviceName: string;
    user_id: number;
    name: string;
    familyAssetId: number;
    familyName: string;
    inspectionTemplateId: number;
    inspectionTemplateName: string;
     currentLocationId: number;
    countryName: string;
    regionId: number;
    regionName: string;
    projectId: number;
    projectName: string;
    date: Date;  // ✅ Make `date` optional
    commonComments: string;
    engineHours: string;
} | null; // ✅ Allow null
}



const InspectionEditingForm: React.FC<InspectionEditingFormProps> = ({
  setEditOpen,
  selectedRowData,   
  inspectionTemplateData,
  
  }) => {
    console.log("Selected Row Data:", selectedRowData); 
    console.log("TE", inspectionTemplateData)
    // ✅ Store editable data in state
    const [editableData, setEditableData] = useState<InspectionColumn[]>(inspectionTemplateData);
     const [templateData, setTemplateData] = useState<{ 
        inspectionColumnId: number;
        inspectionColumnName: string; 
        attachments: attachments;
        checked: boolean; 
        text: string 
      }[]>([]);
  
    // ✅ Handle checkbox toggle
    const handleCheckboxChange = (id: number) => {
      setEditableData((prev) =>
        prev.map((item) =>
          item.inspectionColumnId === id ? { ...item, isChecked: !item.isChecked } : item
        )
      );
    };
  
    // ✅ Handle text field change
    const handleTextChange = (id: number, value: string) => {
      setEditableData((prev) =>
        prev.map((item) =>
          item.inspectionColumnId === id ? { ...item, commands: value } : item
        )
      );
    };
    const dispatch = useDispatch(); // ✅ Move inside the component
  
    // ✅ Handle Save Button
    const handleSave = async () => {
        if (!selectedRowData) {
          console.error("No selected row data available.");
          CustomMessage("No data selected for editing", "error", enqueueSnackbar);
          return;
        }
      
        const payload = {
          inspectionReportingId: selectedRowData.inspectionReportingId ?? 0, 
          user_id: selectedRowData.user_id, 
          name: selectedRowData.name,  
          date: selectedRowData.date,
          service: selectedRowData.service, 
          familyAssetId: selectedRowData.familyAssetId ?? 0, 
          familyName: selectedRowData.familyName ?? "", 
          assetName: selectedRowData.assetName ?? "", 
          inspectionTemplateId: selectedRowData.inspectionTemplateId ?? 0, 
          inspectionTemplateName: selectedRowData.inspectionTemplateName ?? "", 
           currentLocationId: selectedRowData.currentLocationId ?? 0,
          countryName: selectedRowData.countryName ?? "",

          regionId: selectedRowData.regionId ?? 0,
          regionName: selectedRowData.regionName ?? "",

          projectId: selectedRowData.projectId ?? 0,
          projectName: selectedRowData.projectName ?? "",
          commonComments: selectedRowData.commonComments ?? "",
          engineHours: selectedRowData.engineHours ?? "",
          serviceName: selectedRowData.serviceName ?? "",        
      
          details: editableData.map(item => ({
            inspectionColumnId: item.inspectionColumnId ?? 0,  
            isChecked: item.isChecked === true,  // ✅ Ensures `true` if checked, `false` if unchecked
            commands: item.commands || "", 
             inspectionAttachments: Array.isArray(item.inspectionAttachments)
                ? item.inspectionAttachments.map((att: InspectionAttachments) => ({
                    inspectionAttachmentId: att.attachmentId,
                    attachmentId: att.attachmentId,
                    name: att.name,
                    savedAttachmentName: att.savedAttachmentName,
                  }))
                : [],


          })),
        };
      
        console.log("Saving Data:", payload);
      
        try {
          const response = await updateInspectionData(payload);
          console.log("Response:", response);
      
          if (response?.status?.code === 201 || response?.status?.code === 200) {
            CustomMessage(response.status.message, "success", enqueueSnackbar);
            setEditOpen(false);
          } else {
            CustomMessage(response.status.message, "error", enqueueSnackbar);
          }
        } catch (error: any) {
          console.error("Error saving inspection report:", error);
          CustomMessage(error?.message || "Failed to save", "error", enqueueSnackbar);
        } finally {
          dispatch(setLoading(false));
        }
      };
      
      const allowedImageTypes = ["image/jpeg", "image/png", "image/jpg", "image/webp"];
      
      const handleFileUpload = async (
        event: React.ChangeEvent<HTMLInputElement>,
        inspectionColumnId: number
      ) => {
        if (!event.target.files || event.target.files.length === 0) {
          CustomMessage("No file selected", "error", enqueueSnackbar);
          return;
        }

        const fileArray = Array.from(event.target.files);

        // Validate image types
        const invalidFile = fileArray.find(file => !allowedImageTypes.includes(file.type));
        if (invalidFile) {
          CustomMessage(
            `Only image files (JPEG, PNG, JPG, WEBP) are allowed. "${invalidFile.name}" is not valid.`,
            "error",
            enqueueSnackbar
          );
          return;
        }

        dispatch(setLoading(true));

        try {
          const uploadPromises = fileArray.map(async (file) => {
            const formData = new FormData();
            formData.append("file", file);

            const response = await uploadFiles(formData);

            if (response.status.code === 201) {
              const uploaded = response.data;

              // Adjust IDs if needed depending on your API response
              const newAttachment: InspectionAttachments = {
                attachmentId: uploaded.attachmentId,
                inspectionAttachmentId: uploaded.attachmentId,
                name: uploaded.name,
                savedAttachmentName: uploaded.savedAttachmentName,
              };

              return newAttachment;
            } else {
              throw new Error(`Upload failed with status: ${response.status.code}`);
            }
          });

          const uploadedFiles = await Promise.all(uploadPromises);

          setEditableData(prev =>
            prev.map(item =>
              item.inspectionColumnId === inspectionColumnId
                ? {
                    ...item,
                    inspectionAttachments: [...(Array.isArray(item.inspectionAttachments) ? item.inspectionAttachments : []), ...uploadedFiles],
                  }
                : item
            )
          );

          CustomMessage("Image(s) uploaded successfully", "success", enqueueSnackbar);
        } catch (error: any) {
          console.error("Upload error", error);
          CustomMessage(`File upload error: ${error.message}`, "error", enqueueSnackbar);
        } finally {
          dispatch(setLoading(false));
          // Clear file input to allow re-upload of same files if needed
          event.target.value = "";
        }
      };

    const removeFile = (inspectionColumnId: number, inspectionAttachmentId: number) => {
      setEditableData(prevData =>
        prevData.map(item => {
          if (item.inspectionColumnId === inspectionColumnId) {
           
            const attachmentsArray = Array.isArray(item.inspectionAttachments)
              ? item.inspectionAttachments
              : item.inspectionAttachments
              ? [item.inspectionAttachments]
              : [];

            // Filter out the file to remove
            const updatedAttachments = attachmentsArray.filter(
              file => file.inspectionAttachmentId !== inspectionAttachmentId
            );

            // If updatedAttachments is empty, you might want to set to null or empty array based on your logic
            return {
              ...item,
              inspectionAttachments: updatedAttachments,
            };
          }
          return item;
        })
      );
    };
    const handleCancel = () => {
        
      setEditOpen(false); // Close the form after saving
    };
    const userDetails = useSelector((state: RootState) => state.auth);
    

    return (
      <div>
        <Typography variant="h6" align="left" gutterBottom>
            Edit Inspection Data
          </Typography>
        <Card
          style={{
            marginTop: "",
            padding: "20px",
            maxWidth: "", // ⬆️ Increased width for a better layout
            marginLeft: "auto",
            marginRight: "auto",
            boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)", // Softer shadow
            borderRadius: "4px",
          }}
        >
          {/* 🔹 Non-Editable Details Section */}
          {selectedRowData && (
            <Grid
              container
              spacing={2}
              style={{
                marginBottom: "20px",
                padding: "20px",
                background: "#fff",
                borderRadius: "10px",
                width: "100%",
              }}
            >
             
            <Grid container spacing={2} style={{ marginTop: '16px' }}>
              {/* Row 1 */}
              <Grid item xs={4}>
                <Typography variant="body1" noWrap>
                  <strong>Family Name:</strong> {selectedRowData.familyName}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="body1" noWrap>
                  <strong>Asset Name:</strong> {selectedRowData.assetName}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="body1" noWrap>
                  <strong>Name:</strong> {selectedRowData.name}
                </Typography>
              </Grid>

              {/* Row 2 */}
              <Grid item xs={4}>
                <Typography variant="body1" noWrap>
                  <strong>Country:</strong> {selectedRowData.countryName}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="body1" noWrap>
                  <strong>Region:</strong> {selectedRowData.regionName}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="body1" noWrap>
                  <strong>Project:</strong> {selectedRowData.projectName}
                </Typography>
              </Grid>

              {/* Row 3 */}
              <Grid item xs={4}>
                <Typography variant="body1" noWrap>
                  <strong>Inspection Type:</strong> {selectedRowData.serviceName}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="body1" noWrap>
                  <strong>Engine Hours:</strong> {selectedRowData.engineHours}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="body1" noWrap>
                  <strong>Date:</strong>{" "}
                  {selectedRowData.date
                    ? new Date(selectedRowData.date).toLocaleDateString()
                    : "N/A"}
                </Typography>
              </Grid>

              {/* Comments - full width */}
              <Grid item xs={12}>
                <Typography variant="body1">
                  <strong>Comments:</strong> {selectedRowData.commonComments}
                </Typography>
              </Grid>
            </Grid>
                           
            </Grid>
          )}
      
          {/* 🔹 Editable Section with Provided Styling */}
          <Card
            style={{
              marginTop: "",
              padding: "20px",
              maxWidth: "",
              marginLeft: "auto",
              marginRight: "auto",
            }}
          >

<Grid container spacing={2}>
  {editableData.map((item) => (
    <Grid
      item
      xs={12}
      key={item.inspectionColumnId}
      style={{
        display: "flex",
        alignItems: "center",
        gap: "15px",
      }}
    >
      {/* Image Circle */}
      <div
        style={{
          width: "59px",
          height: "59px",
          borderRadius: "50%",
          border: "1px solid #ccc",
          overflow: "hidden",
          backgroundColor: "#f5f5f5",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {item.attachments && item.attachments.attachmentId && (
            <ImagePreview
            alt={`Attachment ${item.attachments.attachmentId}`}
            src={`${FILE_DOWNLOAD_PREVIEW}${item.attachments.attachmentId}`}
            style={{
              width: "59px",
              height: "59px",
              borderRadius: "50%",
              objectFit: "cover"
            }}
            />
          )}
      </div>

      {/* Column Name */}
      <Typography style={{ width: "300px" }}>
        {item.inspectionColumnName}
      </Typography>

      {/* Checkbox */}
      <Checkbox
        checked={item.isChecked}
        onChange={() => handleCheckboxChange(item.inspectionColumnId)}
      />

      {/* Text Field for Commands */}
      <TextField
        value={item.commands || ""}
        onChange={(e) =>
          handleTextChange(item.inspectionColumnId, e.target.value)
        }
        placeholder="Commands"
        variant="outlined"
        size="small"
        style={{ width: "300px" }}
      />

      <Grid item>
                    <FormControl>
                      <input
                        id={`fileUpload-${item.inspectionColumnId}`}
                        type="file"
                        multiple
                        accept="image/*"
                        onChange={(e) => handleFileUpload(e, item.inspectionColumnId)}
                        style={{ fontSize: "14px", width: "200px" }}
                      />
                    </FormControl>

                  </Grid>

                      {(() => {
                      const inspectionAttachments = Array.isArray(item.inspectionAttachments)
                        ? item.inspectionAttachments
                        : item.inspectionAttachments
                        ? [item.inspectionAttachments]
                        : [];

                      return (
                        inspectionAttachments.length > 0 && (
                          <List
                            dense
                            sx={{
                              bgcolor: "background.paper",
                              maxHeight: 200,
                              overflowY: "auto",
                              width: "auto",
                              p: 1,
                              border: "1px solid #eee",
                              borderRadius: 2,
                              flexShrink: 0,
                            }}
                          >
                            {inspectionAttachments.map((file) => {
                              const fileUrl = `${FILE_DOWNLOAD_PREVIEW}${file.inspectionAttachmentId}`;

                              return (
                                <ListItem
                                  key={file.inspectionAttachmentId}
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    p: 0.5,
                                  }}
                                >
                                  {/* Image Preview */}
                                  <Box sx={{ width: 59, height: 59, flexShrink: 0 }}>
                                    <ImagePreview
                                      src={fileUrl}
                                      alt={file?.name || "Image Preview"}
                                      style={{
                                        width: "59px",
                                        height: "59px",
                                        borderRadius: "50%",
                                        objectFit: "cover",
                                      }}
                                    />
                                  </Box>

                                  {/* File Name + Download/Delete */}
                                  <Box display="flex" flexDirection="column" gap={0.5}>
                                    <Typography variant="body2" noWrap sx={{ maxWidth: 120 }}>
                                      {file.name || file.savedAttachmentName}
                                    </Typography>

                                    <Box display="flex" gap={1}>
                                      <IconButton
                                        size="small"
                                        onClick={() => {
                                          const link = document.createElement("a");
                                          link.href = fileUrl;
                                          link.setAttribute("download", file.name || "image");
                                          document.body.appendChild(link);
                                          link.click();
                                          document.body.removeChild(link);
                                        }}
                                      >
                                        <DownloadIcon fontSize="small" color="primary" />
                                      </IconButton>

                                      <IconButton
                                        size="small"
                                        onClick={() =>
                                          removeFile(item.inspectionColumnId, file.inspectionAttachmentId)
                                        }
                                      >
                                        <CloseIcon fontSize="small" color="error" />
                                      </IconButton>
                                    </Box>
                                  </Box>
                                </ListItem>
                              );
                            })}
                          </List>
                        )
                      );
                    })()}
                </Grid>
              ))}
            </Grid>
          </Card>

            <Grid container justifyContent="end" style={{ marginTop: "30px", display: "flex", gap: "15px" }}>
            <Grid item style={{ display: "flex", gap: "15px" }}> {/* ✅ Add gap here */}
                <Button className="btn btn-secondary text-white" variant="outlined" color="secondary" onClick={handleCancel} style={styless.button}>
                Cancel
                </Button>
              {userDetails.role.some(
                r => r.roleName === "Admin" || r.roleName === "New Inspection Write"
              ) && (
                <Button
                  className="btn btn-primary"
                  variant="contained"
                  color="primary"
                  onClick={handleSave}
                  style={styless.button}
                >
                  Submit
                </Button>
              )}
            </Grid>
            </Grid>

        </Card>
      </div>
        
      );
  }
  const styless = {
    label: {
      fontSize: '16px',
      fontWeight: 'bold',
      marginBottom: '5px',
    },
    input: {
      background: '#fff',
      borderRadius: '5px',
      height: '40px',
    },
    button: {
      minWidth: '120px',
      height: '40px',
      padding: '10px 0',
    }
  };
      
  
//     return (
//     <Card
//       style={{
//         marginTop: "30px",
//         padding: "20px",
//         maxWidth: "900px", // ⬆️ Increased width
//         marginLeft: "auto",
//         marginRight: "auto",
//       }}
//     >
//       <Typography variant="h5" style={{ marginBottom: "10px" }}>
//         Edit Inspection Data
//       </Typography>

//       {/* 🔹 Non-Editable Details Section */}
//       {selectedRowData && (
//         <Grid
//           container
//           spacing={2}
//           style={{
//             marginBottom: "20px",
//             padding: "15px",
//             background: "#f0f0f0", // ⬆️ Softer background color
//             borderRadius: "10px",
//             width: "100%", // ⬆️ Full width
//             display: "flex",
//             flexWrap: "wrap",
//           }}
//         >
//           <Grid item xs={4}>
//             <Typography variant="body1">
//               <strong>Family Name:</strong> {selectedRowData.familyName}
//             </Typography>
//           </Grid>
//           <Grid item xs={4}>
//             <Typography variant="body1">
//               <strong>Asset Name:</strong> {selectedRowData.assetName}
//             </Typography>
//           </Grid>
//           <Grid item xs={4}>
//             <Typography variant="body1">
//               <strong>Name:</strong> {selectedRowData.name}
//             </Typography>
//           </Grid>
//         </Grid>
//       )}

//       <Grid container spacing={2}>
//         {editableData.map((item) => (
//           <Grid
//             item
//             xs={12}
//             key={item.inspectionColumnId}
//             style={{ display: "flex", alignItems: "center", gap: "15px" }}
//           >
//             <Typography style={{ width: "300px" }}>{item.inspectionColumnName}</Typography>

//             <Checkbox checked={item.isChecked} onChange={() => handleCheckboxChange(item.inspectionColumnId)} />

//             <TextField
//               value={item.commands || ""}
//               onChange={(e) => handleTextChange(item.inspectionColumnId, e.target.value)}
//               placeholder="Enter value"
//               variant="outlined"
//               size="small"
//               style={{ width: "300px" }}
//             />
//           </Grid>
//         ))}
//       </Grid>

//       <Grid container spacing={2} justifyContent="center" style={{ marginTop: "20px" }}>
//         <Grid item>
//           <Button variant="contained" color="primary" onClick={handleSave}>
//             Save
//           </Button>
//         </Grid>
//       </Grid>
//     </Card>
//   );
// };
    export default InspectionEditingForm;
